/**
 * Clinical Route Classification Utilities for Nutrimus
 * Differentiates Oral, Enteral, and Parenteral nutrition routes.
 */

export const ENTERAL_KEYWORDS = [
  'peptisol', 'entramix', 'diabetasol', 'nephrisol', 'nutrican',
  'enteral', 'sonde', 'fresubin', 'ensure', 'pan-enteral',
  'proten', 'blenderized', 'diben', 'pulmocare', 'nepro',
  'glucerna', 'peptamen', 'nutrison', 'isocal', 'pediasure',
  'sgm enteral', 'nutren', 'supportan', 'survimed', 'caloreen',
  'nutrini', 'novasource', 'optiment', 'ngt', 'ogt', 'gastrostomi',
  'jejunostomi', 'formula enteral'
];

/**
 * Checks whether an item should be classified as Enteral.
 * Respects explicit item.route if present, otherwise checks name keywords.
 */
export const isEnteralItem = (item) => {
  if (!item) return false;
  if (item.route === 'enteral') return true;
  if (item.route === 'oral' || item.route === 'parenteral') return false;
  
  const name = (item.name || '').toLowerCase();
  return ENTERAL_KEYWORDS.some(keyword => name.includes(keyword));
};

/**
 * Determines exact route ('oral' | 'enteral' | 'parenteral') for any item.
 */
export const getItemRoute = (item, isFromParenteralList = false) => {
  if (!item) return 'oral';
  if (isFromParenteralList || item.category === 'parenteral' || item.route === 'parenteral') {
    return 'parenteral';
  }
  if (item.route === 'enteral') return 'enteral';
  if (item.route === 'oral') return 'oral';
  return isEnteralItem(item) ? 'enteral' : 'oral';
};

/**
 * Calculates energy, macro, and percentage splits for oral, enteral, and parenteral routes.
 */
export const calculateRouteSplits = (oralEnteralItems = [], parenteralItems = []) => {
  let oralKcal = 0;
  let oralProtein = 0;
  let oralCarbs = 0;
  let oralFat = 0;
  let oralCount = 0;

  let enteralKcal = 0;
  let enteralProtein = 0;
  let enteralCarbs = 0;
  let enteralFat = 0;
  let enteralCount = 0;

  let parenteralKcal = 0;
  let parenteralProtein = 0;
  let parenteralCarbs = 0;
  let parenteralFat = 0;
  let parenteralCount = (parenteralItems || []).length;

  (oralEnteralItems || []).forEach(item => {
    const route = getItemRoute(item, false);
    const kcal = parseFloat(item.energy_kcal) || 0;
    const p = parseFloat(item.protein_g) || 0;
    const c = parseFloat(item.carbohydrate_g) || 0;
    const f = parseFloat(item.fat_g) || 0;

    if (route === 'enteral') {
      enteralKcal += kcal;
      enteralProtein += p;
      enteralCarbs += c;
      enteralFat += f;
      enteralCount += 1;
    } else {
      oralKcal += kcal;
      oralProtein += p;
      oralCarbs += c;
      oralFat += f;
      oralCount += 1;
    }
  });

  (parenteralItems || []).forEach(item => {
    parenteralKcal += parseFloat(item.energy_kcal) || 0;
    parenteralProtein += parseFloat(item.protein_g) || 0;
    parenteralCarbs += parseFloat(item.carbohydrate_g) || 0;
    parenteralFat += parseFloat(item.fat_g) || 0;
  });

  const totalKcal = oralKcal + enteralKcal + parenteralKcal;
  const oralPct = totalKcal > 0 ? (oralKcal / totalKcal) * 100 : 0;
  const enteralPct = totalKcal > 0 ? (enteralKcal / totalKcal) * 100 : 0;
  const parenteralPct = totalKcal > 0 ? (parenteralKcal / totalKcal) * 100 : 0;

  return {
    oralKcal: Math.round(oralKcal),
    enteralKcal: Math.round(enteralKcal),
    parenteralKcal: Math.round(parenteralKcal),
    totalKcal: Math.round(totalKcal),
    oralPct: parseFloat(oralPct.toFixed(1)),
    enteralPct: parseFloat(enteralPct.toFixed(1)),
    parenteralPct: parseFloat(parenteralPct.toFixed(1)),
    oralProtein: parseFloat(oralProtein.toFixed(1)),
    enteralProtein: parseFloat(enteralProtein.toFixed(1)),
    parenteralProtein: parseFloat(parenteralProtein.toFixed(1)),
    oralCarbs: parseFloat(oralCarbs.toFixed(1)),
    enteralCarbs: parseFloat(enteralCarbs.toFixed(1)),
    parenteralCarbs: parseFloat(parenteralCarbs.toFixed(1)),
    oralFat: parseFloat(oralFat.toFixed(1)),
    enteralFat: parseFloat(enteralFat.toFixed(1)),
    parenteralFat: parseFloat(parenteralFat.toFixed(1)),
    itemCounts: {
      oral: oralCount,
      enteral: enteralCount,
      parenteral: parenteralCount,
    }
  };
};

/**
 * Returns clinical status badge based on route split proportions.
 */
export const getRouteClinicalBadge = (oralPct = 0, enteralPct = 0, parenteralPct = 0) => {
  if (parenteralPct > 70) {
    return {
      label: 'Nutrisi Parenteral Penuh (TPN)',
      color: 'purple',
      badgeClass: 'bg-purple-100 text-purple-800 border-purple-200',
      dotClass: 'bg-purple-500'
    };
  }
  if (parenteralPct >= 20 && (enteralPct + oralPct) >= 20) {
    return {
      label: 'Nutrisi Campuran / Transisi',
      color: 'amber',
      badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
      dotClass: 'bg-amber-500'
    };
  }
  if (enteralPct >= 65) {
    return {
      label: 'Nutrisi Enteral Penuh (Tube/Sonde)',
      color: 'sky',
      badgeClass: 'bg-sky-100 text-sky-800 border-sky-200',
      dotClass: 'bg-sky-500'
    };
  }
  if (oralPct >= 75) {
    return {
      label: 'Asupan Oral Penuh',
      color: 'emerald',
      badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      dotClass: 'bg-emerald-500'
    };
  }
  if (parenteralPct < 15 && enteralPct > 35 && oralPct > 35) {
    return {
      label: 'Transisi Weaning Berhasil',
      color: 'teal',
      badgeClass: 'bg-teal-100 text-teal-800 border-teal-200',
      dotClass: 'bg-teal-500'
    };
  }
  return {
    label: 'Kombinasi Enteral & Oral',
    color: 'blue',
    badgeClass: 'bg-blue-100 text-blue-800 border-blue-200',
    dotClass: 'bg-blue-500'
  };
};
